import {Button, IconButton, openDialog} from 'platform/components';
import {Heading, Hide, HStack, Show, Space} from 'platform/foundation';

import {useSelector} from 'react-redux';

import {isNilOrEmpty} from 'ramda-adjunct';

import {InspectionState, useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {ErrorPage, hasAclDocsWithPermission, usePermissions} from '@dms/shared';
import {DigitalCertificateLink} from '@dms/shared-aftersales';

import {composePath, Nullish, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {selectAudit, selectAuditState, selectStructure} from '../../../store/carAudit/selectors';
import {selectUserSelectedLanguage} from '../../../store/user/selectors';
import {getNextPossibleInspectionState} from '../../../utils/getNextPossibleInspectionState';
import {useConditionContext} from '../hooks/useConditionContext';
import {ReopenInspection} from '../ReopenInspection';
import {getTranslation} from '../utils/getTranslation';
import {AutoFillTemplates} from './AutoFillTemplates';
import {ConditionStatus} from './ConditionStatus';
import {ExportPDF} from './ExportPDF';
import {FinishInspection} from './FinishInspection';
import {InRevision} from './InRevision';
import {InspectionActions} from './InspectionActions';
import {ToFinishingForm} from './ToFinishingForm';
import {ToRevisionInspection} from './ToRevisionInspection';
import {UpdateCondition} from './UpdateCondition';

interface InspectionHeaderProps extends TestIdProps {
  returnBackLink?: () => string;
  inspectionId: string;
  vehicleId: string | Nullish;
  isSinglePage?: boolean;
}

export function InspectionHeader(props: InspectionHeaderProps) {
  const navigate = useNavigate();
  const audit = useSelector(selectAudit);
  const auditState = useSelector(selectAuditState);
  const structure = useSelector(selectStructure);
  const locale = useSelector(selectUserSelectedLanguage);

  const {isDisabledForUser} = useConditionContext();

  const {data: vehicle} = useGetVehicleQuery(
    {vehicleId: props.vehicleId ?? ''},
    {skip: isNilOrEmpty(props.vehicleId)}
  );
  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: props.vehicleId ?? '',
    },
    {skip: isNilOrEmpty(props.vehicleId)}
  );

  const [
    hasCloseInspectionPermission,
    hasUpdateInspectionPermission,
    hasReadInspectionPermission,
    hasReopenInspectionPermission,
  ] = usePermissions({
    permissionKeys: [
      'closeInspection',
      'updateInspection',
      'readInspectionDocument',
      'reopenInspection',
    ],
    scopes: {
      closeInspection: {
        inspectionType: audit?.inspectionType,
        participation: 'SKIP',
      },
      updateInspection: {
        inspectionType: audit?.inspectionType,
        participation: 'SKIP',
        inspectionState: audit?.state,
      },
      readInspectionDocument: {
        branchId: vehicle?.branchId,
        participation: vehicleParticipation,
      },
      reopenInspection: {
        inspectionType: audit?.inspectionType,
        participation: 'SKIP',
      },
    },
  });

  if (!props.vehicleId) {
    return <ErrorPage message={i18n.t('page.vehicle.notifications.notFound')} />;
  }

  const nextPossibleInspectionStates = audit?.state
    ? getNextPossibleInspectionState(audit.state)
    : [];

  const canSendInspectionToRevision =
    hasUpdateInspectionPermission &&
    nextPossibleInspectionStates.includes(InspectionState.TO_REVISION);
  const canSendInspectionToFinishing =
    hasUpdateInspectionPermission &&
    nextPossibleInspectionStates.includes(InspectionState.TO_FINISHING);
  const canCloseInspection =
    hasCloseInspectionPermission &&
    nextPossibleInspectionStates.includes(InspectionState.FINISHED_AUDIT);
  const canSendInspectionToInRevision =
    hasUpdateInspectionPermission &&
    nextPossibleInspectionStates.includes(InspectionState.IN_REVISION);
  const canReopenInspection =
    hasUpdateInspectionPermission &&
    hasReopenInspectionPermission &&
    nextPossibleInspectionStates.includes(InspectionState.IN_PROGRESS) &&
    audit?.state === 'finished_audit';

  const handleBackButton = () => {
    if (props.isSinglePage) {
      return;
    }

    navigate(
      props.returnBackLink?.() ??
        composePath(vehiclesRoutes.conditionsInspections, {params: {id: props.vehicleId}})
    );
  };

  return (
    <>
      <HStack data-testid={suffixTestId('inspectionHeader', props)} justify="space-between">
        <HStack align="center" spacing={2}>
          <Hide when={props.isSinglePage}>
            <IconButton
              size="small"
              onClick={handleBackButton}
              icon="navigation/arrow_back_ios"
              data-testid={suffixTestId('inspectionHeader', props)}
            />
          </Hide>
          <Heading size={3} data-testid={suffixTestId('inspectionHeader-title', props)}>
            {structure ? getTranslation(locale, structure.name) : ''}
          </Heading>
          <ConditionStatus />
        </HStack>
        <HStack align="center" spacing={2}>
          <InspectionActions
            inspectionId={props.inspectionId}
            vehicleId={props.vehicleId}
            inspectionType={audit?.inspectionType}
          />

          <DigitalCertificateLink inspectionId={props.inspectionId} />

          <ExportPDF
            inspectionType={audit?.inspectionType}
            data-testid={suffixTestId('inspectionHeader', props)}
            canExport={hasAclDocsWithPermission(hasReadInspectionPermission)}
          />

          {props.vehicleId && (
            <UpdateCondition
              vehicleId={props.vehicleId}
              data-testid={suffixTestId('inspectionHeader', props)}
            />
          )}

          <AutoFillTemplates
            data-testid={suffixTestId('inspectionHeader', props)}
            shouldUseAuditIdForUpdateTemplate={false}
          />
          <Show when={!isDisabledForUser && auditState && canSendInspectionToRevision}>
            <ToRevisionInspection skipNavigation={props.isSinglePage} />
          </Show>
          <Show when={auditState && canSendInspectionToFinishing}>
            <Button
              data-testid={suffixTestId('inspectionHeader', props)}
              onClick={() =>
                openDialog(<ToFinishingForm />, {
                  title: i18n.t('general.actions.toFinishing'),
                })
              }
              title={i18n.t('general.actions.toFinishing')}
            />
          </Show>
          <Show when={canSendInspectionToInRevision}>
            <InRevision data-testid={suffixTestId('inspectionHeader', props)} />
          </Show>
          <Show when={canCloseInspection}>
            <FinishInspection data-testid={suffixTestId('inspectionHeader', props)} />
          </Show>
          <Show when={canReopenInspection}>
            <ReopenInspection
              inspectionId={props.inspectionId}
              handleBackButton={handleBackButton}
              data-testid={suffixTestId('inspectionHeader', props)}
            />
          </Show>
        </HStack>
      </HStack>
      <Space vertical={5} />
    </>
  );
}
